/**
 * Typography scale (prompt.md §40 `design-tokens`).
 *
 * WHY: conversation transcripts, run logs and settings screens all need to pick
 * a size by *intent* (`body`, `code`, `title`) rather than by pixel value. The
 * sizes are expressed as rem strings so a client can scale the whole product by
 * changing the root font size, and as plain data so the Android theme can map
 * the same names onto Compose `TextStyle`s.
 */

export const FONT_FAMILIES = {
  sans: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
  mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
} as const;

export type FontFamilyToken = keyof typeof FONT_FAMILIES;

export const FONT_SIZES = {
  xs: "0.75rem",
  sm: "0.8125rem",
  md: "0.875rem",
  lg: "1rem",
  xl: "1.25rem",
  "2xl": "1.5rem",
  "3xl": "1.875rem",
} as const;

export type FontSizeToken = keyof typeof FONT_SIZES;

export const FONT_WEIGHTS = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export type FontWeightToken = keyof typeof FONT_WEIGHTS;

/** Unitless multipliers, applied to the font size of the same step. */
export const LINE_HEIGHTS = {
  tight: 1.15,
  snug: 1.3,
  normal: 1.5,
  relaxed: 1.65,
} as const;

export type LineHeightToken = keyof typeof LINE_HEIGHTS;

export const LETTER_SPACINGS = {
  tight: "-0.011em",
  normal: "0em",
  wide: "0.014em",
} as const;

export type LetterSpacingToken = keyof typeof LETTER_SPACINGS;

/**
 * Complete, ready-to-use text styles. Components reference a style such as
 * `body` or `code`; nobody assembles a font triple by hand, which is what keeps
 * transcripts and settings screens visually consistent as the product grows.
 */
export interface TextStyleToken {
  readonly family: FontFamilyToken;
  readonly size: FontSizeToken;
  readonly weight: FontWeightToken;
  readonly lineHeight: LineHeightToken;
  readonly letterSpacing: LetterSpacingToken;
}

export const TEXT_STYLES = {
  body: { family: "sans", size: "md", weight: "regular", lineHeight: "normal", letterSpacing: "normal" },
  bodyStrong: { family: "sans", size: "md", weight: "semibold", lineHeight: "normal", letterSpacing: "normal" },
  secondary: { family: "sans", size: "sm", weight: "regular", lineHeight: "normal", letterSpacing: "normal" },
  caption: { family: "sans", size: "xs", weight: "medium", lineHeight: "normal", letterSpacing: "wide" },
  label: { family: "sans", size: "sm", weight: "medium", lineHeight: "snug", letterSpacing: "normal" },
  title: { family: "sans", size: "xl", weight: "semibold", lineHeight: "snug", letterSpacing: "tight" },
  display: { family: "sans", size: "3xl", weight: "bold", lineHeight: "tight", letterSpacing: "tight" },
  code: { family: "mono", size: "sm", weight: "regular", lineHeight: "relaxed", letterSpacing: "normal" },
} as const satisfies Readonly<Record<string, TextStyleToken>>;

export type TextStyleName = keyof typeof TEXT_STYLES;

/** Resolve a composite style into its individual token names. */
export function resolveTextStyle(name: TextStyleName): TextStyleToken {
  const style = TEXT_STYLES[name];
  if (!style) throw new TypeError(`unknown text style "${name}"`);
  return style;
}
